import { handleAxiosError } from "Helpers/responses";

export default {
  data() {
    return {
      live_feed: window.live_feed, // edit & show only
      live_feed_status: false, // edit & show only
      confirm_modal_action: `start`,
    };
  },

  computed: {
    confirm_modal_message() {
      return `<p>You are about to <strong>${this.confirm_modal_action}</strong> this live feed.</p>
      <p><strong>This action will disconnect live listeners</strong> from your associated stream(s).</p>
      <p class="mt-2">Please confirm to exectue this action.</p>`;
    },
  },

  methods: {

    windowOpen(url, target = null) {
      window.open(url, target);
    },

    getLiveFeedNameTooltip() {
      const content = "Provide a unique name - this name will be used to generate your output mount point names. " +
        "Spaces and special characters will be replaced with underscores and appended with the output bitrate. " +
        "<i>My live stream -> My_live_stream_128.mp3</i>";
      return this.styleToolTip(content);
    },

    getModeTooltip(show_title = true) {
      const title = show_title ? 'Feed Item Ordering' : null;
      const content = `<strong>Normal: </strong>play the ${this.getItemLabel()}s in the order they are represented in the feed.<br>
        <strong>Shuffle: </strong>play the ${this.getItemLabel()}s in a random order every time the live feed is reloaded.<br>
        <strong>Random: </strong>with each play, a random file will be selected from the feed.`;

      return this.styleToolTip(content, title);
    },

    getReloadModeTooltip(show_title = true) {
      const title = show_title ? 'Determines the triggers that will reload the live feed contents' : null;
      const content = '<strong>Never: </strong> reload must be triggered manually.<br>' +
        '<strong>Seconds: </strong>reload will be triggered after X number of seconds.<br>' +
        '<strong>Watch: </strong>reload will be triggered whenever the feed content is changed.';

      return this.styleToolTip(content, title);
    },

    getRoundsTooltip(noun) {
      return this.styleToolTip(`Amount of time in ${noun} with which the feed is reloaded; 0 means never.`);
    },

    getBitrateToolTip() {
      return this.styleToolTip('Values are in kHz. Available bitrates are determined by the output format. Higher bitrates use more bandwidth and are less mobile-friendly.');
    },

    getMountNameToolTip(show_title = true) {
      const title = show_title ? 'Naming your mount' : null;
      const content = 'Give a unique name to your mount. This can be added with out without the output format (mp3/aac).' +
        ' If the mount name is already in use, you will receive an error.';

      return this.styleToolTip(content, title);
    },

    getFormatTooltip(show_title = true) {
      const title = show_title ? 'Selecting a format' : null;
      const content = 'Choose the format for this specific output. If you need multiple i.e. MP3 & AAC then add an additional output.';
      return this.styleToolTip(content, title);
    },

    /**
     * Add styled wrapper around the content
     * @param { string } content
     * @param { string } title
     *
     * @returns {string}
     */
    styleToolTip(content, title = null) {
      let html = '<p class="text-left">';

      if (title) {
        html += `<strong><u>${title}</u></strong><br>`;
      }

      html += content;
      html += '</p>';

      return html;
    },

    /**
     * Retrieve a live feed
     * @param { string } uuid
     *
     * @returns {Promise<null|*>}
     */
    async getLiveFeed(uuid) {
      try {
        const response = await axios.get(`/api/v1/live-feeds/${uuid}`);
        return response.data.data;
      } catch (error) {
        handleAxiosError(error);
        return null;
      }
    },

    /**
     * Start, Stop, Restart action controls
     * @param { string } action
     * @param { Object } live_feed
     * @param { boolean } update_live_feed
     *
     * @returns {Promise<void>}
     */
    async control(action, live_feed, update_live_feed = false) {
      const confirmed = await (async () => {
        if (action !== `start`) {
          this.confirm_modal_action = `${action.charAt(0).toUpperCase()}${action.slice(1)}`;
          const confirm_response = await this.fireModal(`confirm-action-modal`);
          return confirm_response.data.confirm;
        }
        return true;
      })();

      if (confirmed) {
        const response = await axios.post(
          `/api/v1/live-feeds/control/${live_feed.uuid}`,
          {
            action,
          },
        );

        // Workaround for response: {"message":"Server Error"}
        // let action_in_the_past = "restarted";
        // if (action === 'start') {
        //  action_in_the_past = 'started';
        // } else if (action === 'stop') {
        //  action_in_the_past = 'stopped';
        // }

        let portal_response = response.data.message;
        if (response.data.message === 'Server Error') {
          portal_response = `A server error occurred while attempting to ${action} stream. If the issue persists, please contact SharpStream Support.`;
          // portal_response = `Server error occurred. Please refresh the page and double check the status and listen to the feed to see if it has ${action_in_the_past}. Contact SharpStream support if you have any questions.`;
        }
        // --------------------------------------------------

        await this.quickAlert({
          title: _.startCase(action),
          message: _.capitalize(portal_response),
          variant: response.data.status,
        });

        if (update_live_feed) {
          this.live_feed = await this.getLiveFeed(live_feed.uuid);
          this.live_feed_status = this.checkStatus(this.live_feed);
        }
      }
    },

    rebuildPlsFile(live_feed) {
      const playlists = live_feed.premium_config.playlists;
      const feed_uuids = _.map(playlists, 'feed_uuid');

      _.forEach(feed_uuids, (feed_uuid) => {
        axios.patch(`/api/v1/feeds/${feed_uuid}/regenerate-pls`).then(() => {
          toastr.success("PLS file regenerated");
        }).catch((error) => {
          window.handleAxiosError(error);
        });
      });
    },

    /**
     * Determine if on air or not.
     *
     * @param { Object } live_feed
     *
     * @returns {boolean}
     */
    checkStatus(live_feed) {
      if (_.isUndefined(live_feed.start_at) && _.isUndefined(live_feed.stop_at)) {
        return false;
      }

      if (moment(live_feed.start_at).isAfter(moment(live_feed.stop_at))) {
        return true;
      }

      if (moment(live_feed.start_at) && _.isEmpty(live_feed.stop_at)) {
        return true;
      }

      return false;
    },

    /**
     * Delete Live Feed
     *
     * @param { Object } live_feed
     * @param { boolean } redirect
     *
     * @returns {Promise<void>}
     */
    async destroy(live_feed, redirect = false) {
      const res = await this.quickAlert({
        title: `Are you sure?`,
        message: `You won't be able to revert this action!`,
        variant: `error`,
        showCancel: true,
      });

      if (res.data.confirm) {
        const response = await axios.delete(
          `/api/v1/live-feeds/${live_feed.uuid}`,
        );

        let message = _.capitalize(response.data.message);
        if (redirect) {
          message += "\n Redirecting...";
        }

        this.quickAlert({
          title: `Delete`,
          message,
          variant: `success`,
        });
      }

      if (redirect) {
        window.location.href = "/live-feeds";
      }
    },
  },
  filters: {
    output_format_filter: function (value) {
      if (value === 'aac') {
        return 'AAC-HE v2';
      }

      return value;
    },
  },
};
